import * as React from 'react';
import propz from 'propz';
import { Field, Form, Formik, ErrorMessage } from 'formik';
import { AGE_GROUPS } from 'Src/consts/school';
import { TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/tournament';
import { TournamentSchoolInvite } from 'Src/models/invite';
import { GENDER } from 'Src/consts/common';
import { getTeamPlacesLeft } from 'Src/helpers/tournament/tournament';
import { checkIsTeamLimitExceeded } from 'Src/helpers/team/team';
import './AddTeamsForm.scss';

interface Props {
  onSubmit: (data: any) => void;
  onCancel: () => void;
  tournament: TournamentSchoolInvite;
}

export function AddTeamsForm(props: Props) {
  const { onSubmit, onCancel, tournament } = props;
  const { autoEnrollmentTeamsSettings = [] } = tournament;
  const isMixedTeamsAvailable = propz.get(tournament, ['isMixedTeamsAvailable']);
  const ages: number[] = propz.get(tournament, ['ages'], []);
  const gender: string = propz.get(tournament, ['gender'], GENDER.MIXED);
  const isGenderMixed = gender === GENDER.MIXED;
  const isGenderMale = gender === GENDER.MALE_ONLY;
  const isGenderFemale = gender === GENDER.FEMALE_ONLY;
  const maximumNumberTeamsOfSchool: number = propz.get(tournament, ['maximumNumberTeamsOfSchool'], 0);

  const maxPlayersPerTeam = tournament.maxPlayersPerTeam;

  let initialValues: Record<string, any> = {};
  switch (true) {
    case isGenderMixed && !isMixedTeamsAvailable:
      ages.forEach(age => {
        initialValues[`${age}-${GENDER.MALE_ONLY}`] = 0;
        initialValues[`${age}-${GENDER.FEMALE_ONLY}`] = 0;
      });
      break;
    case isGenderMixed && isMixedTeamsAvailable:
      ages.forEach(age => {
        initialValues[`${age}-${GENDER.MIXED}`] = 0;
      });
      break;
    case isGenderMale:
      ages.forEach(age => {
        initialValues[`${age}-${GENDER.MALE_ONLY}`] = 0;
      });
      break;
    case isGenderFemale:
      ages.forEach(age => {
        initialValues[`${age}-${GENDER.FEMALE_ONLY}`] = 0;
      });
      break;
  }

  const renderField = (
    settings: any[],
    maxTeams: number,
    placesLeft: number,
    age: number,
    genderKey: string,
    values: Record<string, any>
  ) => {
    const setting = settings.find(s => s.age === age && s.gender === genderKey);
    const hasLimit = setting?.maximumNumberTeams !== 0;
    const ageGroup = AGE_GROUPS['U5-U18'][age];
    const genderLabel = TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[genderKey];

    const availabilityText = hasLimit
      ? `${placesLeft} space${placesLeft === 1 ? '' : 's'} left`
      : 'Unlimited spaces available';

    const schoolLimitText = maxTeams
      ? `Your school can enter up to ${maxTeams} team${maxTeams === 1 ? '' : 's'} in this category.`
      : '';
    const teamFieldName = `${age}-${genderKey}`;
    const selectedTeamCount = Math.max(0, Number(values[teamFieldName]) || 0);

    return (
      <article key={`${age}-${genderKey}`} className="tournamentTeamsForm__category">
        <header className="tournamentTeamsForm__categoryHead">
          <div>
            <span>Entry category</span>
            <h4>{`${ageGroup}, ${genderLabel}`}</h4>
          </div>
          <span className={hasLimit ? 'tournamentTeamsForm__availability' : 'tournamentTeamsForm__availability isOpen'}>
            {availabilityText}
          </span>
        </header>

        {schoolLimitText ? <p className="tournamentTeamsForm__limit">{schoolLimitText}</p> : null}

        <div className="tournamentTeamsForm__fields">
          <label className="tournamentTeamsForm__field">
            <span>Number of teams</span>
            <Field
              type="number"
              name={teamFieldName}
              min={0}
              max={hasLimit ? Math.min(placesLeft, maxTeams || placesLeft) : maxTeams || undefined}
              inputMode="numeric"
            />
          </label>

          {Number(maxPlayersPerTeam) > 0
            ? Array.from({ length: selectedTeamCount }).map((_, teamIndex) => {
                const studentsFieldName = `${teamFieldName}-students-${teamIndex}`;
                const teamLabel = selectedTeamCount > 1 ? `Team ${String.fromCharCode(65 + teamIndex)}` : 'Team';

                return (
                  <label className="tournamentTeamsForm__field" key={studentsFieldName}>
                    <span>
                      Students in {teamLabel} <small>Maximum {maxPlayersPerTeam}</small>
                    </span>
                    <Field
                      type="number"
                      name={studentsFieldName}
                      min={1}
                      max={maxPlayersPerTeam}
                      aria-required="true"
                    />
                    <ErrorMessage
                      name={studentsFieldName}
                      component="span"
                      className="tournamentTeamsForm__fieldError"
                    />
                  </label>
                );
              })
            : null}
        </div>
      </article>
    );
  };

  const renderAddTeamsFormFields = (values: Record<string, any>) => {
    switch (true) {
      case isGenderMixed && !isMixedTeamsAvailable:
        return ([] as React.ReactNode[]).concat(
          ...ages.map(age => {
            const maleLeft = getTeamPlacesLeft(autoEnrollmentTeamsSettings, age, GENDER.MALE_ONLY);
            const femaleLeft = getTeamPlacesLeft(autoEnrollmentTeamsSettings, age, GENDER.FEMALE_ONLY);
            return [
              renderField(autoEnrollmentTeamsSettings, maximumNumberTeamsOfSchool, maleLeft, age, GENDER.MALE_ONLY, values),
              renderField(autoEnrollmentTeamsSettings, maximumNumberTeamsOfSchool, femaleLeft, age, GENDER.FEMALE_ONLY, values)
            ];
          })
        );
      case isGenderMixed && isMixedTeamsAvailable:
        return ages.map(age => {
          const left = getTeamPlacesLeft(autoEnrollmentTeamsSettings, age, GENDER.MIXED);
          return renderField(autoEnrollmentTeamsSettings, maximumNumberTeamsOfSchool, left, age, GENDER.MIXED, values);
        });
      case isGenderMale:
        return ages.map(age => {
          const left = getTeamPlacesLeft(autoEnrollmentTeamsSettings, age, GENDER.MALE_ONLY);
          return renderField(autoEnrollmentTeamsSettings, maximumNumberTeamsOfSchool, left, age, GENDER.MALE_ONLY, values);
        });
      case isGenderFemale:
        return ages.map(age => {
          const left = getTeamPlacesLeft(autoEnrollmentTeamsSettings, age, GENDER.FEMALE_ONLY);
          return renderField(autoEnrollmentTeamsSettings, maximumNumberTeamsOfSchool, left, age, GENDER.FEMALE_ONLY, values);
        });
    }
  };

  const validate = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};

    const teamValues = Object.keys(values).reduce((acc, key) => {
      if (!key.includes('-students-')) {
        acc[key] = values[key];
      }
      return acc;
    }, {} as Record<string, any>);

    const hasInvalidTeamCount = Object.keys(teamValues).some(key => {
      const count = Number(teamValues[key]);
      return !Number.isInteger(count) || count < 0;
    });
    if (hasInvalidTeamCount) {
      errors.commonError = 'Number of teams should be a whole number of 0 or more';
      return errors;
    }

    if (checkIsTeamLimitExceeded(teamValues, autoEnrollmentTeamsSettings, maximumNumberTeamsOfSchool)) {
      errors.commonError = 'Limit exceeded';
      return errors;
    }

    const totalTeams = Object.keys(teamValues).reduce((sum, key) => sum + Number(teamValues[key] || 0), 0);
    if (maximumNumberTeamsOfSchool > 0 && totalTeams > maximumNumberTeamsOfSchool) {
      errors.commonError = `Your school can enter up to ${maximumNumberTeamsOfSchool} teams in this tournament`;
      return errors;
    }

    const anySpaceLeft = autoEnrollmentTeamsSettings.some(s => {
      const maximumNumberTeams = Number(s.maximumNumberTeams || 0);
      const currentNumberTeams = Number(s.currentNumberTeams || 0);
      return maximumNumberTeams === 0 || maximumNumberTeams - currentNumberTeams > 0;
    });

    const allZero = Object.keys(teamValues).every(key => Number(teamValues[key]) === 0);
    if (anySpaceLeft && allZero) {
      errors.commonError = 'You must specify at least one team to be accepted for the tournament';
      return errors;
    }

    if (Number(maxPlayersPerTeam) > 0) {
      Object.keys(teamValues).forEach(teamFieldName => {
        const teamCount = Number(teamValues[teamFieldName]);
        for (let teamIndex = 0; teamIndex < teamCount; teamIndex += 1) {
          const key = `${teamFieldName}-students-${teamIndex}`;
          const val = values[key];
          const studentsCount = Number(val);
          if (val === '' || !Number.isInteger(studentsCount) || studentsCount < 1) {
            errors[key] = 'Please enter the number of students';
          } else if (studentsCount > Number(maxPlayersPerTeam)) {
            errors[key] = `Max ${maxPlayersPerTeam} students`;
          }
        }
      });
    }

    return errors;
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
      {({ errors, values }) => (
        <Form className="tournamentTeamsForm">
          <div className="tournamentTeamsForm__categories">{renderAddTeamsFormFields(values)}</div>
          {errors.commonError ? <div className="tournamentTeamsForm__error">{String(errors.commonError)}</div> : null}
          <div className="tournamentTeamsForm__actions">
            <button type="button" className="tournamentTeamsForm__button" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="tournamentTeamsForm__button isPrimary">
              Confirm booking
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

